import { useState, useEffect } from 'react';
import {
  Clock, Upload, ScanLine, FileText, ArrowRightLeft, Globe,
  Package, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight,
  Loader2, Wrench, Shield, Cpu, Search, Users, User
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  upload: { icon: Upload, color: 'text-blue-700 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-950/20', border: 'border-blue-300 dark:border-blue-900/30', label: 'Upload' },
  extraction: { icon: ScanLine, color: 'text-emerald-700 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-950/20', border: 'border-emerald-300 dark:border-emerald-900/30', label: 'Extraction' },
  parsing: { icon: FileText, color: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-950/20', border: 'border-amber-300 dark:border-amber-900/30', label: 'Parsing' },
  ai_search: { icon: Globe, color: 'text-violet-700 dark:text-violet-400', bg: 'bg-violet-100 dark:bg-violet-950/20', border: 'border-violet-300 dark:border-violet-900/30', label: 'AI Search' },
  activity: { icon: Users, color: 'text-cyan-700 dark:text-cyan-400', bg: 'bg-cyan-100 dark:bg-cyan-950/20', border: 'border-cyan-300 dark:border-cyan-900/30', label: 'Activity' },
};

const CATEGORY_LABELS = {
  subcontractor_list: 'Sub List',
  drawing_set: 'Drawing Set',
  rfi_log: 'RFI Log',
  change_order: 'Change Order',
  equipment: 'Equipment',
  doc_finder: 'Doc Finder',
  g703_sov: 'G703 SOV',
  submittal: 'Submittal',
  warranty_certificate: 'Warranty Cert',
  other: 'Document',
  rfi: 'RFI',
  team: 'Team',
  intake: 'Intake',
  asset: 'Asset',
  settings: 'Settings',
};

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function StatBox({ value, label, icon: Icon, color }) {
  // Derive light-mode bg from color: text-blue-700 -> bg-blue-100
  const bgClass = color.replace('text-', 'bg-').replace('-700', '-100').replace('-400', '-950/30').split(' ');
  const lightBg = bgClass[0];
  const darkBg = bgClass[1] ? bgClass[1].replace('dark:', '') : lightBg;
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-card border border-border rounded-sm">
      <div className={`shrink-0 w-8 h-8 rounded-sm flex items-center justify-center ${lightBg} ${darkBg ? `dark:${darkBg}` : ''}`}>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <div>
        <p className="text-lg font-mono font-bold text-foreground">{value}</p>
        <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

function TimelineEvent({ event }) {
  const [expanded, setExpanded] = useState(false);
  const config = TYPE_CONFIG[event.type] || TYPE_CONFIG.upload;
  const Icon = config.icon;
  const catLabel = CATEGORY_LABELS[event.category] || event.category;

  return (
    <div
      className={`relative pl-8 pb-4 group`}
      data-testid={`timeline-event-${event.id}`}
    >
      {/* Timeline line */}
      <div className="absolute left-[11px] top-6 bottom-0 w-px bg-secondary group-last:hidden" />

      {/* Timeline dot */}
      <div className={`absolute left-0 top-1.5 w-6 h-6 rounded-full flex items-center justify-center ${config.bg} border ${config.border}`}>
        <Icon className={`w-3 h-3 ${config.color}`} />
      </div>

      {/* Event card */}
      <div
        className={`ml-2 border rounded-sm cursor-pointer transition-all hover:border-border ${
          expanded ? `${config.bg} ${config.border}` : 'bg-card border-border/50'
        }`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-xs text-foreground/80 font-medium">{event.title}</p>
              <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${config.bg} ${config.border} border ${config.color}`}>
                {catLabel}
              </span>
              <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${
                event.status === 'complete' ? 'bg-emerald-100 dark:bg-emerald-950/30 border border-emerald-300 dark:border-emerald-900/30 text-emerald-700 dark:text-emerald-500' :
                event.status === 'identified' ? 'bg-blue-100 dark:bg-blue-950/30 border border-blue-300 dark:border-blue-900/30 text-blue-700 dark:text-blue-500' :
                event.status === 'failed' ? 'bg-red-100 dark:bg-red-950/30 border border-red-300 dark:border-red-900/30 text-red-700 dark:text-red-400' :
                'bg-secondary text-muted-foreground'
              }`}>{event.status}</span>
            </div>
            {event.subtitle && (
              <p className="text-[10px] text-muted-foreground mt-1 truncate">{event.subtitle}</p>
            )}
            {event.performed_by?.name && (
              <p className="text-[10px] text-cyan-600 mt-0.5 flex items-center gap-1">
                <User className="w-2.5 h-2.5" /> {event.performed_by.name}
              </p>
            )}
          </div>
          <span className="text-[10px] text-muted-foreground font-mono shrink-0">{formatTime(event.timestamp)}</span>
          {expanded ? <ChevronDown className="w-3 h-3 text-muted-foreground" /> : <ChevronRight className="w-3 h-3 text-muted-foreground" />}
        </div>

        {/* Expanded details */}
        {expanded && event.details && (
          <div className="px-4 pb-3 pt-1 border-t border-border/30">
            <div className="flex flex-wrap gap-3">
              {event.details.count != null && (
                <DetailChip label="Count" value={event.details.count} />
              )}
              {event.details.with_make_model != null && (
                <DetailChip label="With OEM" value={`${event.details.with_make_model}/${event.details.count}`} />
              )}
              {event.details.verified_urls != null && (
                <DetailChip label="Verified URLs" value={event.details.verified_urls} color="text-emerald-700 dark:text-emerald-400" />
              )}
              {event.details.pm_tasks != null && event.details.pm_tasks > 0 && (
                <DetailChip label="PM Tasks" value={event.details.pm_tasks} color="text-blue-700 dark:text-blue-400" />
              )}
              {event.details.spare_parts != null && event.details.spare_parts > 0 && (
                <DetailChip label="Spare Parts" value={event.details.spare_parts} color="text-rose-700 dark:text-rose-400" />
              )}
              {event.details.source_type && (
                <DetailChip label="Source" value={event.details.source_type} />
              )}
              {event.details.confidence != null && (
                <DetailChip label="Confidence" value={`${(event.details.confidence * 100).toFixed(0)}%`} />
              )}
              {event.details.impacts && (
                <>
                  {event.details.impacts.substituted > 0 && <DetailChip label="Substituted" value={event.details.impacts.substituted} color="text-amber-700 dark:text-amber-400" />}
                  {event.details.impacts.added > 0 && <DetailChip label="Added" value={event.details.impacts.added} color="text-emerald-700 dark:text-emerald-400" />}
                  {event.details.impacts.removed > 0 && <DetailChip label="Removed" value={event.details.impacts.removed} color="text-red-700 dark:text-red-400" />}
                </>
              )}
              {event.details.types?.length > 0 && (
                <div className="w-full">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-widest mb-1">Equipment Types</p>
                  <div className="flex flex-wrap gap-1">
                    {event.details.types.map((t, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 bg-muted/50 border border-border/50 rounded text-muted-foreground">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailChip({ label, value, color = 'text-foreground/80' }) {
  return (
    <div className="text-center">
      <p className={`text-sm font-mono font-bold ${color}`}>{value}</p>
      <p className="text-[9px] text-muted-foreground uppercase tracking-widest">{label}</p>
    </div>
  );
}

function TimelineSummary({ summary }) {
  if (!summary) return null;
  return (
    <div className="grid grid-cols-5 gap-2 mb-6" data-testid="timeline-summary">
      <StatBox value={summary.uploads} label="Uploads" icon={Upload} color="text-blue-700 dark:text-blue-400" />
      <StatBox value={summary.total_assets} label="Equipment" icon={Cpu} color="text-emerald-700 dark:text-emerald-400" />
      <StatBox value={summary.total_change_orders} label="Change Orders" icon={ArrowRightLeft} color="text-amber-700 dark:text-amber-400" />
      <StatBox value={summary.ai_searches} label="AI Lookups" icon={Globe} color="text-violet-700 dark:text-violet-400" />
      <StatBox value={summary.activities || 0} label="Activity" icon={Users} color="text-cyan-700 dark:text-cyan-400" />
    </div>
  );
}

export function TimelineSummaryCompact({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('fmiq_token');
    fetch(`${API_URL}/api/closeout/projects/${projectId}/intake/timeline`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [projectId]);

  if (loading) return <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> Loading timeline...</div>;
  if (!data || data.events?.length === 0) return null;

  const recent = data.events.slice(0, 3);
  return (
    <div className="mt-4 border-t border-border/50 pt-4" data-testid="intake-timeline-compact">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Recent Activity</span>
        <span className="text-[10px] font-mono text-muted-foreground">{data.summary.total_events} events</span>
      </div>
      <div className="space-y-1.5">
        {recent.map(e => {
          const config = TYPE_CONFIG[e.type] || TYPE_CONFIG.upload;
          const Icon = config.icon;
          return (
            <div key={e.id} className="flex items-center gap-2 px-3 py-2 bg-secondary/20 rounded-sm border border-border/30">
              <Icon className={`w-3 h-3 ${config.color} shrink-0`} />
              <span className="text-[10px] text-muted-foreground flex-1 truncate">{e.title}</span>
              <span className="text-[9px] text-muted-foreground font-mono shrink-0">{formatTime(e.timestamp)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function TimelinePanel({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const token = localStorage.getItem('fmiq_token');
    fetch(`${API_URL}/api/closeout/projects/${projectId}/intake/timeline`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [projectId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data || !data.events || data.events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 bg-card border border-dashed border-border rounded-sm">
        <Clock className="w-8 h-8 text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">No intake activity yet</p>
        <p className="text-xs text-muted-foreground">Upload documents in the Smart Intake tab to get started</p>
      </div>
    );
  }

  const filters = [
    { id: 'all', label: 'All', count: data.events.length },
    { id: 'upload', label: 'Uploads', count: data.summary.uploads },
    { id: 'extraction', label: 'Extractions', count: data.summary.extractions },
    { id: 'parsing', label: 'Parsing', count: data.summary.parsings },
    { id: 'ai_search', label: 'AI Search', count: data.summary.ai_searches },
    { id: 'activity', label: 'Activity', count: data.summary.activities || 0 },
  ];

  const filtered = filter === 'all' ? data.events : data.events.filter(e => e.type === filter);

  return (
    <div data-testid="timeline-panel">
      <TimelineSummary summary={data.summary} />

      {/* Filter pills */}
      <div className="flex items-center gap-2 mb-4">
        {filters.map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`timeline-filter-${f.id}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider rounded-sm border transition-colors ${
              filter === f.id
                ? 'bg-blue-100 dark:bg-blue-900/20 text-primary border-primary/30'
                : 'text-muted-foreground border-border/50 hover:text-foreground/80 hover:border-border'
            }`}
          >
            {f.label}
            <span className="font-mono text-[9px] opacity-60">{f.count}</span>
          </button>
        ))}
      </div>

      {/* Timeline */}
      <div className="relative">
        {filtered.map(event => (
          <TimelineEvent key={event.id} event={event} />
        ))}
      </div>
    </div>
  );
}
